/**
 * Facebook JavaScript SDK loader and WhatsApp Embedded Signup helper.
 *
 * Implements the Twilio ISV Tech Provider integration guide:
 * https://www.twilio.com/docs/whatsapp/isv/tech-provider-program/integration-guide
 *
 * Key differences from generic Meta Embedded Signup:
 * - sessionInfoVersion: 3 (required by Twilio)
 * - solutionID in extras.setup (Twilio Partner Solution ID)
 * - FB.login callback is a no-op — all data comes via message event
 * - HTTPS required — Meta won't load SDK on plain HTTP
 */


declare global {
  interface Window {
    FB: {
      init: (params: {
        appId: string
        autoLogAppEvents: boolean
        xfbml: boolean
        version: string
      }) => void
      login: (callback: (response: FacebookLoginResponse) => void, options: Record<string, unknown>) => void
    }
    fbAsyncInit: () => void
  }
}

interface FacebookLoginResponse {
  status: string
  authResponse?: {
    code?: string
  }
}

export interface EmbeddedSignupResult {
  wabaId: string
  phoneNumberId: string
}

export interface EmbeddedSignupPrefill {
  phoneCode?: number
  phoneNumber?: string
  displayName?: string
  verifyByCall?: boolean
}

let sdkReady: Promise<void> | null = null

/**
 * Load the Facebook JS SDK. Safe to call multiple times — only loads once.
 */
export function loadFacebookSDK(appId: string): Promise<void> {
  if (sdkReady) return sdkReady

  sdkReady = new Promise<void>((resolve) => {
    if (window.FB) {
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: true, version: 'v21.0' })
      resolve()
      return
    }

    window.fbAsyncInit = () => {
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: true, version: 'v21.0' })
      resolve()
    }

    const script = document.createElement('script')
    script.id = 'facebook-jssdk'
    script.src = 'https://connect.facebook.net/en_US/sdk.js'
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  })

  return sdkReady
}

/**
 * Launch Meta's Embedded Signup popup for the Twilio ISV flow.
 *
 * All meaningful data (WABA ID, phone number ID) comes via the message
 * event listener — the FB.login() callback is intentionally a no-op
 * per the Twilio integration guide.
 *
 * Must be called from a direct user click handler to avoid popup blockers.
 */
export function launchEmbeddedSignup(
  configId: string,
  solutionId?: string,
  prefill?: EmbeddedSignupPrefill,
  existingWabaId?: string,
  onEvent?: (event: string, data: Record<string, unknown>) => void,
  preverifiedId?: string,
): Promise<EmbeddedSignupResult> {
  return new Promise((resolve, reject) => {
    let settled = false
    const settle = (fn: () => void) => {
      if (!settled) {
        settled = true
        window.removeEventListener('message', handleMessage)
        fn()
      }
    }

    const handleMessage = (event: MessageEvent) => {
      if (!event.origin.endsWith('facebook.com')) return

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
        if (data.type !== 'WA_EMBEDDED_SIGNUP') return

        console.log('WA_EMBEDDED_SIGNUP event', JSON.stringify(data, null, 2))
        onEvent?.(data.event ?? 'UNKNOWN', data.data ?? {})

        if (
          data.event === 'FINISH' ||
          data.event === 'FINISH_ONLY_WABA' ||
          data.event === 'FINISH_GRANT_ONLY_API_ACCESS'
        ) {
          const { phone_number_id, waba_id } = data.data
          console.log('Embedded Signup complete', { phone_number_id, waba_id })
          settle(() => resolve({
            wabaId: waba_id ?? '',
            phoneNumberId: phone_number_id ?? '',
          }))
        } else if (data.event === 'CANCEL') {
          const { current_step } = data.data
          console.warn('Embedded Signup cancelled at', current_step)
          settle(() => reject(new Error('User cancelled WhatsApp setup')))
        } else if (data.event === 'ERROR') {
          const { error_message } = data.data
          console.error('Embedded Signup error', error_message)
          settle(() => reject(new Error(error_message || 'WhatsApp signup error')))
        }
      } catch {
        // Ignore non-JSON messages from other sources
      }
    }

    window.addEventListener('message', handleMessage)

    const setup: Record<string, unknown> = {}
    if (solutionId) {
      setup.solutionID = solutionId
    }
    if (existingWabaId) {
      // Attach the new phone to the customer's existing WABA instead of
      // minting a new one — avoids Twilio 63102 on register_sender when the
      // customer's subaccount is already bound to another WABA.
      setup.waba_id = existingWabaId
    }
    if (prefill) {
      const phone: Record<string, unknown> = {}
      if (prefill.phoneCode) phone.code = prefill.phoneCode
      if (prefill.phoneNumber) phone.number = prefill.phoneNumber
      if (prefill.displayName) phone.displayName = prefill.displayName
      if (prefill.verifyByCall) phone.isVoiceNumber = true
      if (Object.keys(phone).length > 0) setup.phone = phone
    }
    if (preverifiedId) {
      setup.preVerifiedPhone = { ids: [preverifiedId] }
    }

    const extras: Record<string, unknown> = {
      sessionInfoVersion: '3',
    }
    if (prefill?.phoneNumber || preverifiedId) {
      // Show the phone number screen: prefill fills it in, preVerifiedPhone
      // surfaces the pre-verified number so Meta skips its OTP step.
      // only_waba_sharing would bypass the screen entirely.
    } else {
      extras.featureType = 'only_waba_sharing'
    }
    if (Object.keys(setup).length > 0) {
      extras.setup = setup
    }

    const loginOptions = {
      config_id: configId,
      auth_type: 'rerequest',
      response_type: 'code',
      override_default_response_type: true,
      extras,
    }
    console.log('FB.login payload', JSON.stringify(loginOptions, null, 2))
    window.FB.login(
      (response) => {
        // Twilio ISV flow doesn't use the FB.login callback for data —
        // logged for diagnostics only (e.g. popup blocked → status 'unknown').
        console.log('FB.login callback status', response.status)
        if (!settled) {
          settle(() => reject(new Error('User cancelled WhatsApp setup')))
        }
      },
      loginOptions,
    )
  })
}
